/**
 * ID-buffer encoding. One RGBA8 pixel carries 32 bits: 10 bits of "pick object" slot (which pass: entry, component,
 * kind) and 22 bits of item index (atom index, bond index or residue index). Slot 0 is "nothing". The engine owns the
 * slot table (PickObject) and resolves the rest from it.
 */
export const PICK_SLOT_BITS = 10;
export const PICK_INDEX_BITS = 22;
export const PICK_MAX_SLOT = (1 << PICK_SLOT_BITS) - 1;   // 1023
export const PICK_MAX_INDEX = (1 << PICK_INDEX_BITS) - 1; // 4194303

export type PickKind = 'atom' | 'bond' | 'ribbon';

export interface PickObject { slot: number; entrySerial: number; componentSerial: number; kind: PickKind }

export function encodePickId(slot: number, index: number): number {
  if (slot < 1 || slot > PICK_MAX_SLOT) throw new RangeError(`pick slot ${slot} out of range`);
  if (index < 0 || index > PICK_MAX_INDEX) throw new RangeError(`pick index ${index} out of range`);
  return ((slot << PICK_INDEX_BITS) | index) >>> 0;
}

export function decodePickId(id: number): { slot: number; index: number } | null {
  id >>>= 0;
  const slot = id >>> PICK_INDEX_BITS;
  if (slot === 0) return null;
  return { slot, index: id & PICK_MAX_INDEX };
}

/** 32-bit id -> 4 normalised bytes (r, g, b, a) as the shader writes them. */
export function pickIdToBytes(id: number, out: Uint8Array, offset = 0): void {
  out[offset] = (id >>> 24) & 255;
  out[offset + 1] = (id >>> 16) & 255;
  out[offset + 2] = (id >>> 8) & 255;
  out[offset + 3] = id & 255;
}

export function pickIdFromBytes(r: number, g: number, b: number, a: number): number {
  return ((r << 24) | (g << 16) | (b << 8) | a) >>> 0;
}
